import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// Numeric Apple App Store ID from controlled app config. This is the store
// listing ID, not the bundle ID; native production iOS builds must set it.
const iosAppStoreId = String(import.meta.env.VITE_IOS_APP_STORE_ID ?? '').trim();

// One native bridge call per launch is enough; the info never changes at runtime.
// Cache the in-flight promise so concurrent consumers cannot duplicate the bridge call.
let appInfoPromise = null;

export const getAppPlatform = () => Capacitor.getPlatform();

export const getAppInfo = async () => {
  if (!appInfoPromise) {
    appInfoPromise = App.getInfo().catch((error) => {
      appInfoPromise = null;
      throw error;
    });
  }

  return appInfoPromise;
};

const parseVersionSegments = (value) => {
  if (typeof value !== 'string') return null;

  // Versions must use the complete MAJOR.MINOR.PATCH format; anything else
  // is malformed and requires the caller to present a retry action.
  const segments = value.trim().split('.');
  if (segments.length !== 3 || segments.some((segment) => !/^\d+$/.test(segment))) return null;

  return segments.map(BigInt);
};

export const compareAppVersions = (installedVersion, minimumVersion) => {
  const installedSegments = parseVersionSegments(installedVersion);
  const minimumSegments = parseVersionSegments(minimumVersion);
  if (!installedSegments || !minimumSegments) return null;

  for (let index = 0; index < 3; index += 1) {
    if (installedSegments[index] !== minimumSegments[index]) {
      return installedSegments[index] > minimumSegments[index] ? 1 : -1;
    }
  }

  return 0;
};

const isNumericAppStoreId = (value) => typeof value === 'string' && /^\d+$/.test(value.trim());

export const resolveUpdateUrl = ({ platform, appId }) => {
  // The single store-link seam. An empty result means there is no valid
  // update destination, and the gate must not block without one.
  if (platform === 'ios') {
    return isNumericAppStoreId(iosAppStoreId)
      ? `https://apps.apple.com/app/id${iosAppStoreId}`
      : '';
  }

  if (platform === 'android') {
    const packageId = String(appId ?? '').trim();
    return packageId ? `https://play.google.com/store/apps/details?id=${packageId}` : '';
  }

  return '';
};
